import { can, type Role } from '@seller/shared';

/**
 * The sidebar's structure, kept apart from the shell so it can be tested
 * without rendering: the everyday pages first, then a collapsible "Settings"
 * group holding the pages used to feed and administer the data.
 *
 * Account is deliberately not here; it lives in the profile menu at the top
 * right, next to Sign out.
 */

/** The glyph each entry shows; the shell maps a name to its icon component. */
export type NavIconName =
  | 'home'
  | 'box'
  | 'tag'
  | 'restock'
  | 'cart'
  | 'chart'
  | 'upload'
  | 'book'
  | 'copy'
  | 'users'
  | 'mail'
  | 'barcode'
  | 'printer';

export interface NavItemSpec {
  to: string;
  label: string;
  icon: NavIconName;
  /** Match the path exactly, so a parent is not lit up by its children. */
  end?: boolean;
  /** Pages listed under this one, indented: its submenu. */
  children?: NavItemSpec[];
}

/** The Labeling page, under Products, and its per-browser printing setting. */
export const LABELING_PATH = '/products/labeling';
export const LABEL_SETTINGS_PATH = '/settings/labels';

export interface NavSections {
  main: NavItemSpec[];
  settings: NavItemSpec[];
}

/**
 * Navigation is filtered by the same permission check the API enforces, so a
 * Viewer is never shown a link that would only return 403. The check is a
 * convenience, not the control — the server refuses regardless.
 */
export function navSections(role: Role): NavSections {
  const main: NavItemSpec[] = [
    { to: '/', label: 'Home', icon: 'home', end: true },
    { to: '/skus', label: 'SKUs', icon: 'box' },
    {
      to: '/products',
      label: 'Products',
      icon: 'tag',
      // "labeling" can never be read as a product's ASIN (ten upper-case letters
      // and digits), so the path is safe beside a /products/:asin page.
      children: [{ to: LABELING_PATH, label: 'Labeling', icon: 'barcode' }],
    },
    { to: '/restock', label: 'Restock', icon: 'restock' },
    { to: '/purchase-plan', label: 'Purchase plan', icon: 'cart' },
    { to: '/charts', label: 'Charts', icon: 'chart' },
  ];

  const settings: NavItemSpec[] = [
    { to: '/imports', label: 'Imports', icon: 'upload' },
    { to: '/bookings', label: 'Order bookings', icon: 'book' },
    { to: '/duplicates', label: 'Duplicate products', icon: 'copy' },
    // A setting kept in this browser, not on the account: any role may set it.
    { to: LABEL_SETTINGS_PATH, label: 'Label printing', icon: 'printer' },
  ];

  if (can(role, 'users:manage')) {
    settings.push({ to: '/users', label: 'Users', icon: 'users' });
    settings.push({ to: '/email', label: 'Email', icon: 'mail' });
  }

  return { main, settings };
}

/**
 * Whether a path belongs to one of these entries: the entry itself or a page
 * beneath it (`/bookings/…`), never a path that merely starts with the same
 * letters (`/importsx`).
 */
export function pathInItems(pathname: string, items: readonly NavItemSpec[]): boolean {
  return items.some((item) =>
    item.end ? pathname === item.to : pathname === item.to || pathname.startsWith(`${item.to}/`),
  );
}

/** Where the Settings group's open/closed choice is kept, beside `seller.nav`. */
export const SETTINGS_GROUP_STORAGE_KEY = 'seller.nav.settings';

/**
 * The stored choice as a boolean. Nothing stored (or storage unavailable)
 * means collapsed: the group opens by itself whenever a page inside it is
 * shown, so a closed default costs nothing on those pages.
 */
export function parseGroupOpen(stored: string | null): boolean {
  return stored === 'open';
}

/** The value stored for a preference. */
export function serializeGroupOpen(open: boolean): 'open' | 'collapsed' {
  return open ? 'open' : 'collapsed';
}

/**
 * The Settings group's open/closed state.
 *
 * `preference` is the owner's own choice, the only thing ever stored: it changes
 * only when the owner clicks the group's button. `autoOpen` is transient — set
 * while a page inside the group is shown, so that page is visible in the panel —
 * and is never stored, so leaving the group's pages returns it to the preference.
 */
export interface GroupState {
  preference: boolean;
  autoOpen: boolean;
}

export function initialGroupState(preference: boolean, inGroup: boolean): GroupState {
  return { preference, autoOpen: inGroup };
}

export function isGroupOpen(state: GroupState): boolean {
  return state.preference || state.autoOpen;
}

/**
 * A change of page: arriving on a page inside the group opens it for now;
 * leaving the group's pages drops that, back to the preference. Called on every
 * page change, so moving between two Settings pages opens it again even after
 * the owner folded it on the first.
 */
export function groupOnRoute(state: GroupState, inGroup: boolean): GroupState {
  return state.autoOpen === inGroup ? state : { ...state, autoOpen: inGroup };
}

/**
 * The owner's click: fold an open group (auto-opened or not) or unfold a closed
 * one. The result's `preference` is what gets stored.
 */
export function groupOnToggle(state: GroupState): GroupState {
  return { preference: !isGroupOpen(state), autoOpen: false };
}
